import Table from '@eduzz/houston-ui/Table';
import Typography from '@eduzz/houston-ui/Typography';
import Icon from '@material-ui/core/Icon';
import Tooltip from '@material-ui/core/Tooltip';
import moment from 'moment';
import React, { memo, useCallback, useEffect } from 'react';
import { useObservable } from 'react-use-observable';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import { TableActionButton } from 'v2/components/Shared/TableActionButton';
import Truncate from 'v2/components/Shared/TruncateText';
import { formatMoney } from 'v2/helpers/money';
import { EContractStatus } from 'v2/interfaces/subscriptions';
import subscriptionsService from 'v2/services/subscriptions';
import tableService from 'v2/services/table';

import type { IProduct, ISubscriptionInvoice, ISubscriptions } from 'v2/interfaces/subscriptions';

import { CollapseInvoice } from './Collapse';
import { useStyles } from './styles';
import SubscriptionTags from './SubscriptionTags';
import { EDateTypes } from '../Filter/interfaces';

interface IProps {
  handleSetCurrentSubscription: (subscription: ISubscriptions) => void;
  handleSetCurrentInvoice: (invoice: ISubscriptionInvoice) => void;
}

const MySubscriptionsList = memo(({ handleSetCurrentSubscription, handleSetCurrentInvoice }: IProps) => {
  const classes = useStyles();

  useObservable(() => subscriptionsService.load(), []);
  const [isLoading] = useObservable(() => tableService.isLoading(), []);
  const [filter] = useObservable(() => subscriptionsService.getFilter(), []);
  const [subscriptions] = useObservable(() => subscriptionsService.getSubscriptions(), []);
  const [pagination] = useObservable(() => subscriptionsService.getPaginationResponse(), []);

  useEffect(() => {
    return () => subscriptionsService.cleanFilter();
  }, []);

  const onChangeRowsPerPage = useCallback(
    (per_page: number) => {
      subscriptionsService.setPagination({ ...pagination, per_page, current_page: 1 });
      document.getElementById('subscriptions_table').scrollIntoView();
    },
    [pagination]
  );

  const onChangePage = useCallback(
    (page: number) => {
      subscriptionsService.setPagination({ ...pagination, current_page: page });
      document.getElementById('subscriptions_table').scrollIntoView();
    },
    [pagination]
  );

  const formatChargeDate = (subscrition: ISubscriptions) => {
    const { payment_status, next_charge_date } = subscrition;

    if (
      payment_status?.id !== EContractStatus.SUSPENSO &&
      payment_status?.id !== EContractStatus.CANCELADO &&
      payment_status?.id !== EContractStatus.FINALIZADO &&
      next_charge_date !== '-'
    ) {
      return moment(next_charge_date).format('L');
    }

    return '-';
  };

  const renderProductTitle = (product: IProduct) => {
    if (!product) return null;
    const formattedProductTitle = formatProductTitle(product.title || '');
    if (product.title?.length < 40) {
      return (
        <Typography size='xxs' weight='semibold'>
          <Truncate count={40} final='...'>
            {formattedProductTitle}
          </Truncate>
        </Typography>
      );
    }
    return (
      <Tooltip title={formattedProductTitle} placement='top'>
        <Typography size='xxs' weight='semibold'>
          <Truncate count={40} final='...'>
            {formattedProductTitle}
          </Truncate>
        </Typography>
      </Tooltip>
    );
  };

  const formatProductTitle = (title: string) => {
    return title.replace(/^\(\d+\)\s/gi, '') || '';
  };

  function getSubscriptionDate(subscription: ISubscriptions) {
    const dateKey = filter.date_type === EDateTypes.CREATED ? 'initial_date' : EDateTypes.CANCELED;
    const date = subscription[dateKey];
    return date ? moment(date).format('DD/MM/YYYY') : '-';
  }

  const currentIndex = pagination ? (pagination.current_page - 1) * pagination.per_page + 1 : 1;

  const isCancelledDateType = filter?.date_type === EDateTypes.CANCELED;
  return (
    <Table id='subscriptions_table' total={pagination?.total_items || 0} loading={isLoading}>
      <Table.Header>
        <Table.Column width={20}>#</Table.Column>
        <Table.Column width={110}>Contrato</Table.Column>
        <Table.Column width={20}>{isCancelledDateType ? 'Cancelamento' : 'Início'}</Table.Column>
        <Table.Column width={300}>Cliente</Table.Column>
        <Table.Column>Produto</Table.Column>
        <Table.Column width={100}>Cobrança</Table.Column>
        <Table.Column width={150}>Próx. Vencimento</Table.Column>
        <Table.Column width={100} align='right'>
          Valor
        </Table.Column>
        <Table.Column></Table.Column>
      </Table.Header>
      <Table.Body>
        {subscriptions?.map((sub, index) => (
          <Table.Row data={sub} key={sub.id} index={index}>
            <Table.Cell>
              <Typography size='xxs'>{currentIndex + index}.</Typography>
            </Table.Cell>
            <Table.Cell>
              <div className={`${classes.statusCell} status-${sub?.payment_status?.id || ''}`}>
                <Typography size='xxs' color='inherit' weight='semibold'>
                  {sub.id}
                </Typography>
                <Typography className={classes.status} size='xxxs' color='inherit' weight='bold'>
                  {sub?.payment_status?.name || ''}
                </Typography>
                <Typography>
                  <SubscriptionTags size='small' subscription={sub} spacing={1} />
                </Typography>
              </div>
            </Table.Cell>
            <Table.Cell>
              <Typography size='xxs'>{getSubscriptionDate(sub)}</Typography>
            </Table.Cell>
            <Table.Cell>
              <div>
                <Typography size='xxs' weight='semibold'>
                  <Truncate count={25} final='...'>
                    {sub?.customer?.business_name || ''}
                  </Truncate>
                </Typography>
                <PermissionHide action='myeduzz:meus-contratos:ver-dados-clientes'>
                  <Typography size='xxs'>
                    <Truncate count={25} final='...'>
                      {sub?.customer?.email || ''}
                    </Truncate>
                  </Typography>
                </PermissionHide>
              </div>
            </Table.Cell>
            <Table.Cell>
              <div>
                {renderProductTitle(sub.product)}
                <Typography size='xxs'>({sub?.product?.id || ''})</Typography>
              </div>
            </Table.Cell>
            <Table.Cell>
              <Typography size='xxs'>
                {sub.payment_charges} / {sub?.cycles || '∞'}
              </Typography>
            </Table.Cell>
            <Table.Cell>
              <Typography size='xxs'>{formatChargeDate(sub)}</Typography>
            </Table.Cell>
            <Table.Cell align='right'>
              <Typography weight='semibold'>
                <PermissionHide action='myeduzz:meus-contratos:ocultar-valores' inverse fallback={<em>oculto</em>}>
                  {formatMoney(sub.total_price)}{' '}
                  {sub.currency && sub.currency !== 'BRL' && (
                    <Tooltip placement='top' title='O valor da venda é convertido para Reais conforme cotação da moeda da venda na data da cobrança.'>
                      <Icon style={{ fontSize: 14, display: 'inline-block' }}>info</Icon>
                    </Tooltip>
                  )}
                </PermissionHide>
              </Typography>
            </Table.Cell>
            <Table.Cell align='right'>
              <TableActionButton onClick={() => handleSetCurrentSubscription(sub)} />
            </Table.Cell>

            <CollapseInvoice subscription={sub} handleSetCurrentInvoice={handleSetCurrentInvoice} />
          </Table.Row>
        ))}
      </Table.Body>
      {!isLoading && pagination && (
        <Table.Pagination
          page={pagination?.current_page}
          perPage={pagination?.per_page}
          onChangePage={onChangePage}
          onChangePerPage={onChangeRowsPerPage}
          optionsPerPage='10,20,50,100'
        />
      )}
    </Table>
  );
});

export default MySubscriptionsList;
